import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IGetRevenueGroupsItem, IRevenueGroup, RevenueGroupKind, VehicleSalesType, WorkorderLineType } from '@cbgms/api'
import { useQuery } from '@cbgms/base/http/use-query'
import { fields as listFields, query as listQuery } from '@cbgms/components/list'
import { formatEnum } from '@cbgms/base/utils/helpers'
import { CountryCode } from '@carsys/enums/country-code'
import { loadRevenueGroups } from 'app/settings/state/RevenueGroups/actions'
import { IRootReducerState } from 'state/rootReducer'
import { Time } from '@carsys/enums/time'

interface AllOptionsItem {
  lineType: WorkorderLineType
  results: IGetRevenueGroupsItem[]
}

interface AllOptionsItemVehicleSales {
  lineType: VehicleSalesType
  results: IGetRevenueGroupsItem[]
}

export interface LineTypeItem {
  lineType: WorkorderLineType
  vehicleSalesType: VehicleSalesType
  uuid: string
  options: {
    value: string
    label: string
    orig: IRevenueGroup
  }[]
  revenueGroupKind: RevenueGroupKind
  label: string
}

export const useGetConfigureDefaultRevenueGroups = (addRevenueGroup: boolean) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const country = useSelector<IRootReducerState>(state => state.auth.shop.VisitorCountry)

  const createLineTypeConfig = (lineType: WorkorderLineType, defaults: IGetRevenueGroupsItem[], allOptions: AllOptionsItem[]) => {
    const uuid =
      defaults.find(a => a.RevenueGroup.ProductType === lineType && a.RevenueGroup.IsDefaultForProductType)?.RevenueGroup?.UUID || ''
    const options = allOptions.find(a => a.lineType === lineType)?.results || []
    const dropdownOptions = options
      .filter(a => a.RevenueGroup.ProductType === lineType)
      .map((a: IGetRevenueGroupsItem) => ({
        value: a.RevenueGroup.UUID,
        label: a.RevenueGroup.Name,
        orig: a.RevenueGroup
      }))
    const label = `${formatEnum('EnumWorkorderLineType', lineType)} ${t('setting.accountancy_revenue_groups_line_type')}`

    return {
      lineType,
      vehicleSalesType: VehicleSalesType.None,
      uuid,
      options: dropdownOptions,
      revenueGroupKind: RevenueGroupKind.Workshop,
      label
    }
  }

  const createLineTypeConfigVehicleSales = (
    lineType: VehicleSalesType,
    defaults: IGetRevenueGroupsItem[],
    allOptions: AllOptionsItemVehicleSales[]
  ) => {
    const uuid =
      defaults.find(a => a.RevenueGroup.VehicleSalesType === lineType && a.RevenueGroup.IsDefaultForProductType)?.RevenueGroup?.UUID || ''
    const options = allOptions.find(a => a.lineType === lineType)?.results || []
    const dropdownOptions = options
      .filter(a => a.RevenueGroup.VehicleSalesType === lineType)
      .map((a: IGetRevenueGroupsItem) => ({
        value: a.RevenueGroup.UUID,
        label: a.RevenueGroup.Name,
        orig: a.RevenueGroup
      }))
    const label = `${formatEnum('EnumRevenueVehicleSalesType', lineType)}`

    return {
      lineType: WorkorderLineType.None,
      vehicleSalesType: lineType,
      uuid,
      options: dropdownOptions,
      revenueGroupKind: RevenueGroupKind.CarSales,
      label
    }
  }

  return useQuery(
    ['ConfigureDefaultsContainer', addRevenueGroup],
    async () => {
      const revenueGroups = await dispatch(
        loadRevenueGroups({
          fields: listFields<IGetRevenueGroupsItem>()
            .add('RevenueGroup.Name')
            .add('RevenueGroup.UUID')
            .add('RevenueGroup.ProductType')
            .add('RevenueGroup.VehicleSalesType')
            .add('RevenueGroup.IsDefaultForProductType')
            .add('RevenueGroup.Kind')
            .toString(),
          search: listQuery<IGetRevenueGroupsItem>().equals('RevenueGroup.IsDefaultForProductType', true).toString()
        })
      )
      const funcGetLineTypeOptions = async (lineType: WorkorderLineType) => {
        const result = await dispatch(
          loadRevenueGroups({
            fields: listFields<IGetRevenueGroupsItem>().addAllOf('RevenueGroup').toString(),
            search: listQuery<IGetRevenueGroupsItem>().equals('RevenueGroup.ProductType', lineType).toString()
          })
        )
        return { lineType, results: result.Data }
      }

      const funcGetLineTypeOptionsVehicleSales = async (lineType: VehicleSalesType) => {
        const result = await dispatch(
          loadRevenueGroups({
            fields: listFields<IGetRevenueGroupsItem>().addAllOf('RevenueGroup').toString(),
            search: listQuery<IGetRevenueGroupsItem>().equals('RevenueGroup.VehicleSalesType', lineType).toString()
          })
        )
        return { lineType, results: result.Data }
      }

      // take all line types besides Text/Remark. (GMS-12117)
      const allLineTypesWorkOrder = Object.values(WorkorderLineType).filter(
        a => a !== WorkorderLineType.Text && a !== WorkorderLineType.None
      )
      const allOptionsWorkOrder = await Promise.all(allLineTypesWorkOrder.map(funcGetLineTypeOptions))
      const lineTypes = allLineTypesWorkOrder.map(lineType => createLineTypeConfig(lineType, revenueGroups.Data, allOptionsWorkOrder))

      // take all the vehicle sales line types
      // const allLineTypesVehicleSales = Object.values(VehicleSalesType).filter(a => a !== VehicleSalesType.None)
      const allLineTypesVehicleSales = Object.values(VehicleSalesType).filter(a =>
        country === CountryCode.NL
          ? a !== VehicleSalesType.None
          : a !== VehicleSalesType.None && a !== VehicleSalesType.Bpm && a !== VehicleSalesType.BpmNew
      )
      const allOptionsVehicleSales = await Promise.all(allLineTypesVehicleSales.map(funcGetLineTypeOptionsVehicleSales))
      const vehicleSalesTypes = allLineTypesVehicleSales.map(lineTypeVehicleSales =>
        createLineTypeConfigVehicleSales(lineTypeVehicleSales, revenueGroups.Data, allOptionsVehicleSales)
      )

      // add the vehicle sales lines to the workorder lines.
      vehicleSalesTypes.forEach(vehicleSalesType => {
        lineTypes.push(vehicleSalesType)
      })

      return lineTypes
    },
    {
      staleTime: 30 * Time.Second
    }
  )
}
